import { type ReactElement, type ReactNode, useId } from 'react';

import { Icon } from '@/shared/ui/icon';

import styles from './Dialog.module.scss';
import { useDialog } from './use-dialog';

interface DialogProps {
  open: boolean;
  title: string;
  onClose: () => void;
  children: ReactNode;
  variant?: 'modal' | 'drawer';
}

function DialogHeader({
  title,
  titleId,
  onClose,
}: Pick<DialogProps, 'title' | 'onClose'> & { titleId: string }): ReactElement {
  return (
    <header className={styles.header}>
      <h2 id={titleId} className={styles.title}>
        {title}
      </h2>
      <button type="button" className={styles.close} onClick={onClose}>
        <span className="visually-hidden">Закрыть</span>
        <Icon name="close" />
      </button>
    </header>
  );
}

/** Native modal dialog: focus trap, Escape and focus return come from the browser. */
export function Dialog({
  open,
  title,
  onClose,
  children,
  variant = 'modal',
}: DialogProps): ReactElement {
  const ref = useDialog(open, onClose);
  const titleId = useId();

  return (
    <dialog
      ref={ref}
      className={variant === 'drawer' ? styles.drawer : styles.dialog}
      aria-labelledby={titleId}
      onClose={onClose}
      onCancel={(event) => {
        event.preventDefault();
        onClose();
      }}
    >
      <DialogHeader title={title} titleId={titleId} onClose={onClose} />
      <div className={styles.body}>{open && children}</div>
    </dialog>
  );
}
